import {
  ASSET_STATUSES,
  ASSET_TEXT_LIMITS,
  WORK_ORDER_KINDS,
  WORK_ORDER_STATUSES,
} from '@wodi/domain';
import { z } from 'zod';

import { actorSchema, isoDaySchema, reasonSchema } from './clinical-common.ts';
import { centsStringSchema } from './money.ts';
import { capturedListValueSchema, operationsListItemSchema } from './operations.ts';

// Assets and maintenance (docs/plans/phase3-operations-modules.md §3.2). Money is integer
// cents as a string, in KES. URLs carry internal IDs and the status filter only.

const L = ASSET_TEXT_LIMITS;
const optional = (max: number) => z.string().trim().max(max).nullable();
const named = z.object({ id: z.uuid(), name: z.string() });

export const assetOptionsSchema = z.object({
  categories: z.array(operationsListItemSchema),
  suppliers: z.array(named),
  /** Active stores at this facility, for parts. */
  stores: z.array(named),
});
export type AssetOptionsView = z.infer<typeof assetOptionsSchema>;

const assetDetailFields = {
  name: z.string().trim().min(1).max(L.name),
  categoryCode: z.string().trim().min(1).max(40),
  makeModel: optional(L.makeModel),
  serialNumber: optional(L.serial),
  location: optional(L.location),
  department: optional(L.department),
  supplierId: z.uuid().nullable(),
  purchaseDate: isoDaySchema.nullable(),
  purchaseCostCents: centsStringSchema.nullable(),
  warrantyUntil: isoDaySchema.nullable(),
  serviceIntervalDays: z.number().int().min(1).max(3650).nullable(),
};

export const registerAssetRequestSchema = z.object({
  tag: z.string().trim().min(1).max(30),
  ...assetDetailFields,
});
export type RegisterAssetRequest = z.infer<typeof registerAssetRequestSchema>;

export const updateAssetRequestSchema = z.object(assetDetailFields);
export type UpdateAssetRequest = z.infer<typeof updateAssetRequestSchema>;

export const changeAssetStatusRequestSchema = z.object({
  status: z.enum(ASSET_STATUSES),
  reason: reasonSchema.nullable(),
});
export type ChangeAssetStatusRequest = z.infer<typeof changeAssetStatusRequestSchema>;

export const MAINTENANCE_DUE_STATES = ['overdue', 'due_soon'] as const;

export const assetListItemSchema = z.object({
  id: z.uuid(),
  /** Mono. */
  tag: z.string(),
  name: z.string(),
  category: capturedListValueSchema,
  location: z.string().nullable(),
  department: z.string().nullable(),
  status: z.enum(ASSET_STATUSES),
  nextServiceDue: isoDaySchema.nullable(),
  dueState: z.enum(MAINTENANCE_DUE_STATES).nullable(),
  openWorkOrders: z.number().int(),
});
export type AssetListItemView = z.infer<typeof assetListItemSchema>;

export const assetListSchema = z.object({ items: z.array(assetListItemSchema) });
export type AssetListView = z.infer<typeof assetListSchema>;

export const ASSET_LIST_FILTERS = ['in_service', 'out_of_service', 'condemned', 'all'] as const;

export const workOrderListItemSchema = z.object({
  id: z.uuid(),
  /** Mono. */
  workOrderNumber: z.string(),
  asset: z.object({ id: z.uuid(), tag: z.string(), name: z.string() }),
  kind: z.enum(WORK_ORDER_KINDS),
  status: z.enum(WORK_ORDER_STATUSES),
  problem: z.string(),
  openedAt: z.iso.datetime(),
  completedAt: z.iso.datetime().nullable(),
});
export type WorkOrderListItemView = z.infer<typeof workOrderListItemSchema>;

export const workOrderListSchema = z.object({ items: z.array(workOrderListItemSchema) });
export type WorkOrderListView = z.infer<typeof workOrderListSchema>;

export const WORK_ORDER_LIST_FILTERS = ['open', 'closed', 'all'] as const;

export const assetSchema = assetListItemSchema.extend({
  makeModel: z.string().nullable(),
  serialNumber: z.string().nullable(),
  supplier: named.nullable(),
  purchaseDate: isoDaySchema.nullable(),
  purchaseCostCents: z.string().nullable(),
  warrantyUntil: isoDaySchema.nullable(),
  serviceIntervalDays: z.number().int().nullable(),
  lastServicedOn: isoDaySchema.nullable(),
  statusReason: z.string().nullable(),
  statusChangedAt: z.iso.datetime().nullable(),
  registeredAt: z.iso.datetime(),
  workOrders: z.array(workOrderListItemSchema),
  /** Incidents that name this asset, for linking a breakdown. */
  incidents: z.array(
    z.object({ id: z.uuid(), incidentNumber: z.string(), occurredAt: z.iso.datetime() }),
  ),
});
export type AssetView = z.infer<typeof assetSchema>;

export const openWorkOrderRequestSchema = z.object({
  clientRequestId: z.uuid(),
  kind: z.enum(WORK_ORDER_KINDS),
  problem: z.string().trim().min(1).max(L.problem),
  incidentId: z.uuid().nullable(),
  /** A breakdown may take the asset out of service as it is reported. */
  takeOutOfService: z.boolean(),
});
export type OpenWorkOrderRequest = z.infer<typeof openWorkOrderRequestSchema>;

export const completeWorkOrderRequestSchema = z.object({
  workDone: z.string().trim().min(1).max(L.workDone),
  /** Put an out-of-service asset back in service. */
  returnToService: z.boolean(),
});
export const cancelWorkOrderRequestSchema = z.object({ reason: reasonSchema });

export const addWorkOrderPartRequestSchema = z.object({
  storeId: z.uuid(),
  itemId: z.uuid(),
  quantity: z.number().int().min(1).max(100_000),
  /** A batch chosen over the earliest-expiring one, with the reason. */
  batchId: z.uuid().nullable(),
  batchOverrideReason: z.string().trim().min(5).max(500).nullable(),
});
export type AddWorkOrderPartRequest = z.infer<typeof addWorkOrderPartRequestSchema>;

export const workOrderPartSchema = z.object({
  id: z.uuid(),
  item: named,
  store: named,
  /** Mono. */
  batchNumber: z.string(),
  expiryDate: isoDaySchema.nullable(),
  quantity: z.number().int(),
  unit: z.string(),
  recordedAt: z.iso.datetime(),
  recordedBy: actorSchema,
});

export const workOrderSchema = workOrderListItemSchema.extend({
  asset: z.object({
    id: z.uuid(),
    tag: z.string(),
    name: z.string(),
    status: z.enum(ASSET_STATUSES),
  }),
  incident: z.object({ id: z.uuid(), incidentNumber: z.string() }).nullable(),
  tookOutOfService: z.boolean(),
  openedBy: actorSchema,
  startedAt: z.iso.datetime().nullable(),
  workDone: z.string().nullable(),
  completedBy: actorSchema.nullable(),
  statusReason: z.string().nullable(),
  parts: z.array(workOrderPartSchema),
});
export type WorkOrderView = z.infer<typeof workOrderSchema>;

/** Items with stock in a store, for choosing a part. */
export const partOptionsSchema = z.object({
  items: z.array(
    z.object({
      itemId: z.uuid(),
      name: z.string(),
      unit: z.string(),
      available: z.number().int(),
      lots: z.array(
        z.object({
          batchId: z.uuid(),
          batchNumber: z.string(),
          expiryDate: isoDaySchema.nullable(),
          quantity: z.number().int(),
        }),
      ),
    }),
  ),
});
export type PartOptionsView = z.infer<typeof partOptionsSchema>;
